import * as bootstrap from 'bootstrap';

window.bootstrap = bootstrap;

/**
 * NeuralHR front-end behaviour.
 *
 * Deliberately small. The application is server rendered, so this file only
 * handles what genuinely needs the browser: the sidebar, confirmation before
 * consequential actions, and revealing values the server has gated.
 */

// -----------------------------------------------------------------------------
// Sidebar
// -----------------------------------------------------------------------------

const SIDEBAR_STATE_KEY = 'neuralhr.sidebar.collapsed';

function initSidebar() {
    const shell = document.querySelector('[data-neural-shell]');

    if (!shell) {
        return;
    }

    // The collapsed preference is a per-browser convenience, so localStorage is
    // the right home for it. Access is guarded because a browser set to block
    // site data throws rather than returning null.
    try {
        if (localStorage.getItem(SIDEBAR_STATE_KEY) === '1') {
            shell.classList.add('neural-shell--collapsed');
        }
    } catch {
        // Nothing to do: the sidebar simply starts expanded.
    }

    document.querySelectorAll('[data-neural-sidebar-toggle]').forEach((button) => {
        button.addEventListener('click', () => {
            const isDesktop = window.matchMedia('(min-width: 992px)').matches;

            if (isDesktop) {
                const collapsed = shell.classList.toggle('neural-shell--collapsed');

                try {
                    localStorage.setItem(SIDEBAR_STATE_KEY, collapsed ? '1' : '0');
                } catch {
                    // The preference simply will not persist.
                }

                return;
            }

            shell.classList.toggle('neural-shell--open');
            toggleBackdrop(shell);
        });
    });
}

function toggleBackdrop(shell) {
    const existing = document.querySelector('.neural-backdrop');

    if (existing) {
        existing.remove();

        return;
    }

    if (!shell.classList.contains('neural-shell--open')) {
        return;
    }

    const backdrop = document.createElement('div');
    backdrop.className = 'neural-backdrop';
    backdrop.addEventListener('click', () => {
        shell.classList.remove('neural-shell--open');
        backdrop.remove();
    });

    document.body.appendChild(backdrop);
}

// -----------------------------------------------------------------------------
// Confirmation
// -----------------------------------------------------------------------------

/**
 * Intercepts submission of any form carrying data-confirm and states plainly
 * what is about to happen before it happens.
 *
 * Used for archiving, suspending, voiding a pay rate and clearing someone's
 * second factor. The message comes from the markup so each one says what that
 * particular action does, rather than asking a generic "are you sure".
 */
function initConfirmations() {
    document.addEventListener('submit', (event) => {
        const form = event.target;

        if (!(form instanceof HTMLFormElement) || !form.dataset.confirm) {
            return;
        }

        if (form.dataset.confirmed === 'true') {
            return;
        }

        event.preventDefault();

        if (window.confirm(form.dataset.confirm)) {
            form.dataset.confirmed = 'true';
            form.submit();
        }
    });
}

// -----------------------------------------------------------------------------
// Submit state
// -----------------------------------------------------------------------------

/**
 * Disables a submit button once its form is on its way.
 *
 * A double-clicked save on an employee form is a duplicate employee number and
 * a failed insert; on a pay rate it is two overlapping compensation records.
 */
function initSubmitGuards() {
    document.addEventListener('submit', (event) => {
        const form = event.target;

        if (!(form instanceof HTMLFormElement) || form.dataset.noSubmitGuard) {
            return;
        }

        // Let validation fire first. Disabling on an invalid form would leave
        // the user looking at a dead button.
        if (form.checkValidity && !form.checkValidity()) {
            return;
        }

        form.querySelectorAll('button[type="submit"]').forEach((button) => {
            const label = button.dataset.submittingLabel || 'Saving...';

            // Deferred so the button's own value still reaches the server.
            window.setTimeout(() => {
                button.disabled = true;
                button.innerHTML =
                    '<span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>' +
                    label;
            }, 0);
        });
    });
}

// -----------------------------------------------------------------------------
// Revealing gated values
// -----------------------------------------------------------------------------

/**
 * Fetches the unmasked government identifiers for an employee.
 *
 * The masked form is all the page ever ships. The real values are fetched on
 * demand from a permission-checked endpoint that records the disclosure, so
 * nothing sensitive sits in the HTML waiting to be read out of a cached page or
 * a screenshot.
 */
function initRevealControls() {
    document.querySelectorAll('[data-neural-reveal]').forEach((button) => {
        button.addEventListener('click', async () => {
            const container = document.querySelector(button.dataset.neuralRevealTarget);

            if (!container) {
                return;
            }

            button.disabled = true;
            button.textContent = 'Revealing...';

            try {
                const response = await fetch(button.dataset.neuralReveal, {
                    headers: { Accept: 'application/json' },
                    credentials: 'same-origin',
                });

                if (!response.ok) {
                    throw new Error('Request refused');
                }

                const values = await response.json();

                container.querySelectorAll('[data-identifier]').forEach((element) => {
                    const value = values[element.dataset.identifier];
                    element.textContent = value || 'Not recorded';
                    element.classList.remove('neural-masked');
                });

                button.textContent = 'Revealed. This was recorded in the audit log.';
                button.classList.add('text-muted');
            } catch {
                button.disabled = false;
                button.textContent = 'Could not reveal. You may not have permission.';
            }
        });
    });
}

// -----------------------------------------------------------------------------
// Colour pickers
// -----------------------------------------------------------------------------

/**
 * Keeps the hex text field and the colour swatch on the branding form in step,
 * in both directions.
 */
function initColorInputs() {
    document.querySelectorAll('[data-neural-color-pair]').forEach((group) => {
        const swatch = group.querySelector('input[type="color"]');
        const text = group.querySelector('input[type="text"]');

        if (!swatch || !text) {
            return;
        }

        swatch.addEventListener('input', () => {
            text.value = swatch.value.toUpperCase();
        });

        text.addEventListener('change', () => {
            if (/^#[0-9A-Fa-f]{6}$/.test(text.value)) {
                swatch.value = text.value;
            }
        });
    });
}

// -----------------------------------------------------------------------------
// Charts
// -----------------------------------------------------------------------------

/**
 * Renders the dashboard charts.
 *
 * Chart.js is imported on demand so the pages with no chart on them do not pay
 * for it. The data arrives in a JSON script tag rather than in an inline
 * script, which keeps it out of executable context.
 */
const chartInstances = [];

async function initCharts() {
    const canvases = document.querySelectorAll('[data-neural-chart]');

    if (canvases.length === 0) {
        return;
    }

    const { Chart, registerables } = await import('chart.js');
    Chart.register(...registerables);

    // Anything drawn by a previous run, so switching theme redraws rather than
    // stacking a second chart on the same canvas.
    chartInstances.forEach((chart) => chart.destroy());
    chartInstances.length = 0;

    /*
     | Every colour comes from the theme, not just the two brand ones.
     |
     | The grid and tick colours used to be literals, which meant a chart drawn
     | in dark mode ruled near-white lines across a near-black card. A chart is
     | as much a part of the theme as the card it sits in.
     */
    const styles = getComputedStyle(document.documentElement);
    const read = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;

    const primary = read('--neural-primary', '#0080FF');
    const accent = read('--neural-accent', '#457D58');
    const grid = read('--neural-border', '#e2e8f0');
    const tick = read('--neural-text-muted', '#64748b');
    const fill = `rgba(${read('--neural-primary-rgb', '0, 128, 255')}, 0.14)`;

    canvases.forEach((canvas) => {
        const source = document.getElementById(canvas.dataset.neuralChart);

        if (!source) {
            return;
        }

        const payload = JSON.parse(source.textContent);
        const type = canvas.dataset.neuralChartType || 'bar';

        chartInstances.push(new Chart(canvas, {
            type,
            data: {
                labels: payload.labels,
                datasets: [
                    {
                        label: payload.label || '',
                        data: payload.values,
                        backgroundColor:
                            type === 'line'
                                ? fill
                                : payload.values.map((_, index) =>
                                      index % 2 === 0 ? primary : accent
                                  ),
                        borderColor: type === 'line' ? primary : 'transparent',
                        borderWidth: type === 'line' ? 2 : 0,
                        borderRadius: type === 'bar' ? 4 : 0,
                        fill: type === 'line',
                        tension: 0.3,
                        pointBackgroundColor: primary,
                        pointRadius: 3,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: '#0f172a',
                        padding: 10,
                        cornerRadius: 6,
                        displayColors: false,
                    },
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: { color: tick, font: { size: 11 } },
                    },
                    y: {
                        beginAtZero: true,
                        border: { display: false },
                        grid: { color: grid },
                        ticks: {
                            color: tick,
                            font: { size: 11 },
                            // Headcount is a whole number of people. A y-axis
                            // offering 2.5 employees is not helpful.
                            precision: 0,
                        },
                    },
                },
            },
        }));
    });
}

// -----------------------------------------------------------------------------
// Time clock
// -----------------------------------------------------------------------------

/**
 * Keeps the clock face ticking.
 *
 * Rendered server-side first so the page is correct before any script runs; this
 * only stops it going stale while somebody sits on the screen deciding.
 */
function initClock() {
    const face = document.querySelector('[data-clock]');

    if (!face) {
        return;
    }

    const render = () => {
        face.textContent = new Date().toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    render();
    window.setInterval(render, 1000);
}

/**
 * Attaches a position to a punch, at the moment it is made and at no other.
 *
 * Three things worth noting about how this behaves.
 *
 * The browser asks the employee for permission, every time, through its own
 * prompt. There is no way for this page to read a position without that.
 *
 * A refusal does not block the punch. The form submits without coordinates and
 * the server records the punch as unchecked, because an employee who is at work
 * and cannot say so has a worse problem than a supervisor with a flag to review.
 *
 * The same applies to a timeout. Waiting forever for a fix that a basement or a
 * thick roof will never produce would strand somebody at a form.
 */
function initGeolocatedForms() {
    document.querySelectorAll('form[data-geolocate]').forEach((form) => {
        form.addEventListener('submit', (event) => {
            if (form.dataset.locationResolved === 'true' || !navigator.geolocation) {
                return;
            }

            event.preventDefault();

            const submitter = event.submitter;
            const finish = () => {
                form.dataset.locationResolved = 'true';

                // Re-submitting loses which button was pressed, so it is put
                // back as a hidden field before the form goes.
                if (submitter && submitter.name) {
                    const carried = document.createElement('input');
                    carried.type = 'hidden';
                    carried.name = submitter.name;
                    carried.value = submitter.value;
                    form.appendChild(carried);
                }

                form.submit();
            };

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    form.querySelector('[name="latitude"]').value = position.coords.latitude;
                    form.querySelector('[name="longitude"]').value = position.coords.longitude;
                    form.querySelector('[name="accuracy_meters"]').value =
                        Math.round(position.coords.accuracy);

                    finish();
                },
                () => finish(),
                { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
            );
        });
    });
}

// -----------------------------------------------------------------------------
// Platform appearance
// -----------------------------------------------------------------------------

/**
 * The appearance form: shows only the fields the chosen background needs, and
 * echoes the identity fields into the preview panel as they are typed.
 *
 * The preview updates text and the primary colour live. It does not preview an
 * uploaded file, because the file has not been uploaded yet, and a preview
 * drawn from a local object URL would show something the server has not
 * accepted and might reject.
 */
function initAppearanceForm() {
    const select = document.querySelector('[data-appearance-kind]');

    if (!select) {
        return;
    }

    const hint = document.querySelector('[data-appearance-kind-hint]');
    const hints = JSON.parse(select.dataset.appearanceHints || '{}');

    const applyMode = () => {
        const kind = select.value;

        document.querySelectorAll('[data-appearance-when]').forEach((element) => {
            const modes = element.dataset.appearanceWhen.split(' ');
            element.hidden = !modes.includes(kind);
        });

        if (hint && hints[kind]) {
            hint.textContent = hints[kind];
        }
    };

    select.addEventListener('change', applyMode);
    applyMode();

    // --- Live text and colour ------------------------------------------------

    const preview = document.querySelector('[data-appearance-preview]');

    if (!preview) {
        return;
    }

    const targets = {
        name: preview.querySelector('[data-appearance-preview-initials]'),
        headline: preview.querySelector('[data-appearance-preview-headline]'),
        tagline: preview.querySelector('[data-appearance-preview-tagline]'),
    };

    const nameField = document.querySelector('[data-appearance-bind="name"]');
    const headlineField = document.querySelector('[data-appearance-bind="headline"]');
    const taglineField = document.querySelector('[data-appearance-bind="tagline"]');
    const primaryField = document.querySelector('[data-appearance-bind="primary"]');

    const sync = () => {
        const name = (nameField?.value || '').trim();

        if (targets.name && name) {
            const words = name.split(/\s+/).filter(Boolean);
            targets.name.textContent = (
                words.length > 1 ? words[0][0] + words[1][0] : name.slice(0, 2)
            ).toUpperCase();
        }

        if (targets.headline) {
            targets.headline.textContent =
                (headlineField?.value || '').trim() || name || targets.headline.textContent;
        }

        if (targets.tagline) {
            targets.tagline.textContent =
                (taglineField?.value || '').trim() || taglineField?.placeholder || '';
        }

        // Only for the modes the primary colour actually drives. Over an image
        // or a chosen colour it would show something the saved page will not.
        if (primaryField && select.value === 'brand' && /^#[0-9A-Fa-f]{6}$/.test(primaryField.value)) {
            preview.style.setProperty('--preview-canvas', primaryField.value);
        }
    };

    [nameField, headlineField, taglineField, primaryField].forEach((field) => {
        field?.addEventListener('input', sync);
    });

    select.addEventListener('change', sync);
    sync();
}

// -----------------------------------------------------------------------------
// Copy to clipboard
// -----------------------------------------------------------------------------

/**
 * The copy button beside a value shown once and never again, such as a freshly
 * issued API token.
 *
 * Falls back to selecting the text when the clipboard is unavailable, which it
 * is over plain HTTP and in some locked-down browsers. Selecting is not as good
 * as copying, but it is a great deal better than a button that silently does
 * nothing while somebody walks away believing they have the value.
 */
function initCopyButtons() {
    document.querySelectorAll('[data-copy-target]').forEach((button) => {
        button.addEventListener('click', async () => {
            const field =
                button.dataset.copyTarget === 'previous'
                    ? button.previousElementSibling
                    : document.querySelector(button.dataset.copyTarget);

            if (!field) {
                return;
            }

            const restore = button.textContent;

            try {
                await navigator.clipboard.writeText(field.value ?? field.textContent);
                button.textContent = 'Copied';
            } catch {
                field.focus();
                field.select?.();
                button.textContent = 'Selected, press Ctrl+C';
            }

            window.setTimeout(() => {
                button.textContent = restore;
            }, 2500);
        });
    });
}

// -----------------------------------------------------------------------------
// Theme
// -----------------------------------------------------------------------------

const THEME_KEY = 'neuralhr.theme';

/**
 * The light and dark switch.
 *
 * The theme is already applied by the inline script in the page head, before
 * anything paints. This only handles the button and keeps the choice.
 *
 * Choosing a theme opts out of the operating system's. Somebody who has never
 * chosen keeps following it, including when they change it while the page is
 * open — which is the behaviour a reader expects and almost nothing implements.
 */
function initTheme() {
    const root = document.documentElement;
    const media = window.matchMedia('(prefers-color-scheme: dark)');

    const stored = () => {
        try {
            return window.localStorage.getItem(THEME_KEY);
        } catch {
            // Private browsing, or site data blocked.
            return null;
        }
    };

    const apply = (theme) => {
        root.setAttribute('data-bs-theme', theme);
    };

    document.querySelectorAll('[data-neural-theme-toggle]').forEach((button) => {
        button.addEventListener('click', () => {
            const next = root.getAttribute('data-bs-theme') === 'dark' ? 'light' : 'dark';

            apply(next);

            // Charts take their grid and tick colours from the theme at draw
            // time, so they are redrawn rather than left ruling light-theme
            // lines across a dark card.
            initCharts();

            try {
                window.localStorage.setItem(THEME_KEY, next);
            } catch {
                // The theme still applies for this page. It just will not
                // survive a reload, which is better than refusing to switch.
            }
        });
    });

    // Only while no explicit choice has been made.
    media.addEventListener('change', (event) => {
        if (!stored()) {
            apply(event.matches ? 'dark' : 'light');
            initCharts();
        }
    });
}

// -----------------------------------------------------------------------------
// Sidebar sections
// -----------------------------------------------------------------------------

const NAV_KEY = 'neuralhr.nav.collapsed';

/**
 * Remembers which sidebar sections somebody folded away.
 *
 * The folding itself is <details>, which needs no script at all. This adds
 * persistence, and one rule on top of it: the section containing the current
 * page is always open. Somebody who folds Payroll away and then opens a payroll
 * run should not find the sidebar pointing at nothing.
 *
 * Only the closed sections are stored. A new section added to the product in a
 * later release is then open by default rather than inheriting a decision
 * nobody made about it.
 */
function initNavSections() {
    const sections = Array.prototype.slice.call(
        document.querySelectorAll('[data-neural-nav-section]'),
    );

    if (sections.length === 0) {
        return;
    }

    const read = () => {
        try {
            const raw = window.localStorage.getItem(NAV_KEY);
            const parsed = raw ? JSON.parse(raw) : [];

            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    const write = (keys) => {
        try {
            window.localStorage.setItem(NAV_KEY, JSON.stringify(keys));
        } catch {
            // The fold still works for this page.
        }
    };

    const collapsed = new Set(read());

    sections.forEach((section) => {
        const key = section.dataset.neuralNavSection;

        // The server already opened this one and it stays open.
        if (section.hasAttribute('data-neural-nav-current')) {
            return;
        }

        if (collapsed.has(key)) {
            section.open = false;
        }
    });

    sections.forEach((section) => {
        section.addEventListener('toggle', () => {
            const key = section.dataset.neuralNavSection;

            if (section.open) {
                collapsed.delete(key);
            } else {
                collapsed.add(key);
            }

            write(Array.from(collapsed));
        });
    });
}

// -----------------------------------------------------------------------------
// Boot
// -----------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', () => {
    initTheme();
    initNavSections();
    initSidebar();
    initConfirmations();
    initSubmitGuards();
    initRevealControls();
    initColorInputs();
    initCharts();
    initClock();
    initGeolocatedForms();
    initAppearanceForm();
    initCopyButtons();

    document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
        new bootstrap.Tooltip(element);
    });
});
